import { Routes } from '@shared/constants/routes';
import { getDefaultOrganizationRoute } from '@shared/lib/permissions';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import { authActions, authSelectors } from '@shared/redux/ducks/auth';
import { Flex, Spin } from 'antd';
import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

const getPositiveNumericParam = (raw?: string) => {
  if (!raw) return null;

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;

  return parsed;
};

export const OrganizationRouteOutlet = () => {
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector(authSelectors.selectUserInfo);
  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);
  const { organizationId: organizationIdParam } = useParams<{ organizationId?: string }>();

  const organizationId = useMemo(
    () => getPositiveNumericParam(organizationIdParam),
    [organizationIdParam],
  );

  const availableOrganizations = userInfo?.organizations ?? [];
  const targetOrganization =
    availableOrganizations.find((organization) => organization.id === organizationId) ?? null;
  const fallbackOrganization =
    availableOrganizations.find((organization) => organization.id === currentOrganization?.id) ??
    availableOrganizations[0] ??
    null;

  useEffect(() => {
    if (!targetOrganization) return;
    if (currentOrganization?.id === targetOrganization.id) return;

    dispatch(authActions.setCurrentOrganizationInfo(targetOrganization));
  }, [currentOrganization?.id, dispatch, targetOrganization]);

  if (!organizationId || !targetOrganization) {
    if (!fallbackOrganization?.id) {
      return <Navigate to={Routes.profile.organization.root} replace />;
    }

    return (
      <Navigate
        to={getDefaultOrganizationRoute(fallbackOrganization.permission, fallbackOrganization.id)}
        replace
      />
    );
  }

  if (currentOrganization?.id !== targetOrganization.id) {
    return (
      <Flex align="center" justify="center" style={{ height: '100vh' }}>
        <Spin />
      </Flex>
    );
  }

  return <Outlet />;
};
